import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bestellung, BestellungPosition, Partner, Artikel } from '../types';
import KatalogSuchePanel from './KatalogSuchePanel';
import BelegDokumentView from './BelegDokumentView';
import AktivitaetenBlock from './AktivitaetenBlock';
import './BestellungForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface BestellungFormProps {
  editingBestellung?: Bestellung | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface NeueZeile {
  artikel_id: string;
  menge: string;
  preis_einzeln: string;
}

const leereZeile = (): NeueZeile => ({ artikel_id: '', menge: '1', preis_einzeln: '' });

const BestellungForm: React.FC<BestellungFormProps> = ({ editingBestellung, onSuccess, onCancel }) => {
  const isEdit = !!editingBestellung;
  const statusBearbeitbar = !isEdit || editingBestellung?.status === 'Entwurf';

  const [lieferantId, setLieferantId] = useState<string>(
    editingBestellung ? String(editingBestellung.lieferant_id) : ''
  );
  const [liefertermin, setLiefertermin] = useState<string>(
    editingBestellung?.liefertermin_soll ? editingBestellung.liefertermin_soll.slice(0, 10) : ''
  );
  const [notizen, setNotizen] = useState<string>(editingBestellung?.notizen || '');
  const [status, setStatus] = useState<string>(editingBestellung?.status || 'Entwurf');

  const [lieferanten, setLieferanten] = useState<Partner[]>([]);
  const [artikelListe, setArtikelListe] = useState<Artikel[]>([]);

  // Neuanlage: Positionen werden lokal gehalten und mit dem POST-Request
  // (Kopf + Positions-Array) in einem Request angelegt.
  const [neueZeilen, setNeueZeilen] = useState<NeueZeile[]>([leereZeile()]);

  // Bearbeitung: bestehende Positionen einer geladenen Bestellung, jede
  // Änderung wird sofort über die Positions-CRUD-Endpoints persistiert.
  const [positionen, setPositionen] = useState<BestellungPosition[]>(editingBestellung?.positionen || []);
  const [zeileWirdGespeichert, setZeileWirdGespeichert] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Umschalter Maske/Dokument (nur im Edit-Modus) — Muster analog AuftragForm.
  const [ansichtModus, setAnsichtModus] = useState<'maske' | 'dokument'>('maske');

  // Reload nach Rückkehr aus der Dokumentansicht bzw. bei einem 409-Konflikt
  // (Positions-Statusgate serverseitig ausgelöst) — Muster analog
  // AuftragForm.reloadAuftrag.
  const reloadBestellung = useCallback(async () => {
    if (!editingBestellung?.bestellung_id) return;
    try {
      const res = await axios.get<Bestellung>(
        `${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}`
      );
      const frisch = res.data;
      setStatus(frisch.status);
      setLiefertermin(frisch.liefertermin_soll ? frisch.liefertermin_soll.slice(0, 10) : '');
      setNotizen(frisch.notizen || '');
      setPositionen(frisch.positionen || []);
    } catch {
      // Reload selbst fehlgeschlagen (z.B. Netzwerk) — die Fehlermeldung aus
      // der ursprünglichen 409-Antwort bleibt sichtbar; der Nutzer kann über
      // "Abbrechen" die Maske verlassen.
    }
  }, [editingBestellung]);

  const ladeStammdaten = useCallback(() => {
    Promise.all([
      axios.get<Partner[]>(`${API_URL}/api/partners`),
      axios.get<Artikel[]>(`${API_URL}/api/artikel`),
    ]).then(([pRes, aRes]) => {
      setLieferanten(pRes.data.filter(p => p.typ === 'Lieferant' || p.typ === 'Beides'));
      setArtikelListe(aRes.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    ladeStammdaten();
  }, [ladeStammdaten]);

  const artikelName = (id: number) => artikelListe.find(a => a.artikel_id === id)?.bezeichnung ?? `#${id}`;
  const artikelPreisEk = (id: number) => artikelListe.find(a => a.artikel_id === id)?.preis_ek;

  // ---- Neuanlage: lokale Zeilen ----

  const zeileAendern = (index: number, feld: keyof NeueZeile, wert: string) => {
    setNeueZeilen(prev => prev.map((z, i) => (i === index ? { ...z, [feld]: wert } : z)));
  };

  const zeileHinzufuegen = () => setNeueZeilen(prev => [...prev, leereZeile()]);

  const zeileEntfernen = (index: number) => {
    setNeueZeilen(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const neuGesamtsumme = neueZeilen.reduce((sum, z) => {
    const menge = parseFloat(z.menge) || 0;
    const preis = z.preis_einzeln ? parseFloat(z.preis_einzeln) : (artikelPreisEk(parseInt(z.artikel_id)) || 0);
    return sum + menge * preis;
  }, 0);

  const handleSubmitNeu = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!lieferantId) {
      setError('Lieferant ist erforderlich');
      return;
    }
    const gueltigeZeilen = neueZeilen.filter(z => z.artikel_id && parseFloat(z.menge) > 0);
    if (gueltigeZeilen.length === 0) {
      setError('Mindestens eine Position mit Artikel und Menge ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/bestellungen`, {
        lieferant_id: parseInt(lieferantId),
        liefertermin_soll: liefertermin || undefined,
        notizen: notizen || undefined,
        positionen: gueltigeZeilen.map(z => ({
          artikel_id: parseInt(z.artikel_id),
          menge: parseFloat(z.menge),
          preis_einzeln: z.preis_einzeln ? parseFloat(z.preis_einzeln) : undefined,
        })),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Anlegen der Bestellung');
    } finally {
      setSaving(false);
    }
  };

  // ---- Bearbeitung: Kopf ----

  const handleSubmitKopf = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBestellung) return;
    setError('');
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}`, {
        status,
        liefertermin_soll: liefertermin || null,
        notizen: notizen || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // ---- Bearbeitung: Positionen (sofort persistiert) ----

  const [neuePosition, setNeuePosition] = useState<NeueZeile>(leereZeile());

  const positionHinzufuegen = async () => {
    if (!editingBestellung || !neuePosition.artikel_id || !(parseFloat(neuePosition.menge) > 0)) return;
    setError('');
    setZeileWirdGespeichert(-1);
    try {
      const res = await axios.post<BestellungPosition>(
        `${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}/positionen`,
        {
          artikel_id: parseInt(neuePosition.artikel_id),
          menge: parseFloat(neuePosition.menge),
          preis_einzeln: neuePosition.preis_einzeln ? parseFloat(neuePosition.preis_einzeln) : undefined,
        }
      );
      setPositionen(prev => [...prev, res.data]);
      setNeuePosition(leereZeile());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Hinzufügen der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const positionAendern = async (pos: BestellungPosition, menge: number, preisEinzeln: number) => {
    if (!editingBestellung) return;
    setError('');
    setZeileWirdGespeichert(pos.position_id);
    try {
      const res = await axios.put<BestellungPosition>(
        `${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}/positionen/${pos.position_id}`,
        { menge, preis_einzeln: preisEinzeln }
      );
      setPositionen(prev => prev.map(p => (p.position_id === pos.position_id ? res.data : p)));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Aktualisieren der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const positionLoeschen = async (pos: BestellungPosition) => {
    if (!editingBestellung) return;
    setError('');
    setZeileWirdGespeichert(pos.position_id);
    try {
      await axios.delete(
        `${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}/positionen/${pos.position_id}`
      );
      setPositionen(prev => prev.filter(p => p.position_id !== pos.position_id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const formatPrice = (price?: number) => (price === null || price === undefined ? '-' : `${price.toFixed(2)} €`);

  return (
    <div className="bestellung-form-container">
      <h2>{isEdit ? `Bestellung ${editingBestellung?.bestellung_nr} bearbeiten` : 'Neue Bestellung'}</h2>

      {error && <div className="error-message">{error}</div>}

      {isEdit && (
        <div className="ansicht-umschalter">
          <span className="ansicht-umschalter__label">Ansicht</span>
          <div className="ansicht-umschalter__tabs" role="group" aria-label="Ansicht wechseln">
            <button type="button" aria-pressed={ansichtModus === 'maske'}
              className={`ansicht-umschalter__tab${ansichtModus === 'maske' ? ' is-active' : ''}`}
              onClick={() => { setAnsichtModus('maske'); reloadBestellung(); }}>
              Maske
            </button>
            <button type="button" aria-pressed={ansichtModus === 'dokument'}
              className={`ansicht-umschalter__tab${ansichtModus === 'dokument' ? ' is-active' : ''}`}
              onClick={() => setAnsichtModus('dokument')}>
              Dokument
            </button>
          </div>
        </div>
      )}

      {ansichtModus === 'maske' && (
      <>
      <form onSubmit={isEdit ? handleSubmitKopf : handleSubmitNeu} className="bestellung-form">
        <div className="form-row">
          <div className="form-group">
            <label>Lieferant *</label>
            <select
              value={lieferantId}
              onChange={e => setLieferantId(e.target.value)}
              disabled={isEdit}
              required
            >
              <option value="">-- Lieferant wählen --</option>
              {lieferanten.map(l => (
                <option key={l.partner_id} value={l.partner_id}>{l.name}</option>
              ))}
            </select>
          </div>

          {isEdit && (
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {/* Serverseitig gesperrt: aus BESTELLT/TEILGELIEFERT/ABGESCHLOSSEN/
                    STORNIERT darf nicht zurück auf ENTWURF gesetzt werden (hebelt
                    sonst das Positions-Statusgate aus). Option daher nur zeigen,
                    wenn die Bestellung ohnehin noch in ENTWURF ist. */}
                {editingBestellung?.status === 'Entwurf' && <option value="Entwurf">Entwurf</option>}
                <option value="Bestellt">Bestellt</option>
                <option value="Teilgeliefert">Teilgeliefert</option>
                <option value="Abgeschlossen">Abgeschlossen</option>
                <option value="Storniert">Storniert</option>
              </select>
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Liefertermin (Soll)</label>
            <input type="date" value={liefertermin} onChange={e => setLiefertermin(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Notizen</label>
          <textarea value={notizen} onChange={e => setNotizen(e.target.value)} rows={2} />
        </div>

        {!isEdit && (
          <div className="positionsgrid-section">
            <h3>Positionen</h3>
            <KatalogSuchePanel onUebernommen={ladeStammdaten} />
            <table className="positionsgrid-table">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Menge</th>
                  <th>Einzelpreis (EUR)</th>
                  <th>Gesamt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {neueZeilen.map((z, i) => {
                  const menge = parseFloat(z.menge) || 0;
                  const preis = z.preis_einzeln ? parseFloat(z.preis_einzeln) : (artikelPreisEk(parseInt(z.artikel_id)) || 0);
                  return (
                    <tr key={i}>
                      <td>
                        <select value={z.artikel_id} onChange={e => zeileAendern(i, 'artikel_id', e.target.value)}>
                          <option value="">-- Artikel wählen --</option>
                          {artikelListe.map(a => (
                            <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung} ({a.einheit})</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number" step="0.01" min="0.01" className="menge-input"
                          value={z.menge} onChange={e => zeileAendern(i, 'menge', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number" step="0.01" min="0" className="preis-input"
                          placeholder={artikelPreisEk(parseInt(z.artikel_id))?.toFixed(2) ?? '0.00'}
                          value={z.preis_einzeln} onChange={e => zeileAendern(i, 'preis_einzeln', e.target.value)}
                        />
                      </td>
                      <td className="price-cell">{formatPrice(menge * preis)}</td>
                      <td>
                        <button type="button" className="btn-delete-small" onClick={() => zeileEntfernen(i)} title="Zeile entfernen">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button type="button" className="k-btn k-btn--sm" onClick={zeileHinzufuegen}>+ Position hinzufügen</button>
            <div className="gesamtsumme-zeile">Gesamtsumme: <strong>{formatPrice(neuGesamtsumme)}</strong></div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">Abbrechen</button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Speichere...' : isEdit ? 'Kopf speichern' : 'Bestellung anlegen'}
          </button>
        </div>
      </form>

      {isEdit && (
        <div className="positionsgrid-section">
          <h3>Positionen</h3>
          {!statusBearbeitbar && (
            <div className="hinweis-box">Positionen können nur im Status „Entwurf" geändert werden.</div>
          )}
          <KatalogSuchePanel onUebernommen={ladeStammdaten} />
          <table className="positionsgrid-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Menge</th>
                <th>Einheit</th>
                <th>Einzelpreis (EUR)</th>
                <th>Gesamt</th>
                <th>Geliefert</th>
                {statusBearbeitbar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {positionen.map(pos => (
                <tr key={pos.position_id}>
                  <td>{pos.artikel_bezeichnung || artikelName(pos.artikel_id)}</td>
                  <td>
                    <input
                      type="number" step="0.01" min="0.01" className="menge-input"
                      defaultValue={pos.menge}
                      disabled={!statusBearbeitbar || zeileWirdGespeichert === pos.position_id}
                      onBlur={e => {
                        const neueMenge = parseFloat(e.target.value);
                        if (neueMenge > 0 && neueMenge !== pos.menge) {
                          positionAendern(pos, neueMenge, pos.preis_einzeln);
                        }
                      }}
                    />
                  </td>
                  <td>{pos.artikel_einheit || '-'}</td>
                  <td>
                    <input
                      type="number" step="0.01" min="0" className="preis-input"
                      defaultValue={pos.preis_einzeln}
                      disabled={!statusBearbeitbar || zeileWirdGespeichert === pos.position_id}
                      onBlur={e => {
                        const neuerPreis = parseFloat(e.target.value);
                        if (!isNaN(neuerPreis) && neuerPreis !== pos.preis_einzeln) {
                          positionAendern(pos, pos.menge, neuerPreis);
                        }
                      }}
                    />
                  </td>
                  <td className="price-cell">{formatPrice(pos.preis_gesamt)}</td>
                  <td>{pos.menge_geliefert} / {pos.menge}</td>
                  {statusBearbeitbar && (
                    <td>
                      <button
                        type="button" className="btn-delete-small"
                        disabled={zeileWirdGespeichert === pos.position_id}
                        onClick={() => positionLoeschen(pos)}
                        title="Position löschen"
                      >✕</button>
                    </td>
                  )}
                </tr>
              ))}
              {positionen.length === 0 && (
                <tr><td colSpan={7} className="empty-hint">Noch keine Positionen vorhanden.</td></tr>
              )}
            </tbody>
          </table>

          {statusBearbeitbar && (
            <div className="add-position-row">
              <select value={neuePosition.artikel_id} onChange={e => setNeuePosition(p => ({ ...p, artikel_id: e.target.value }))}>
                <option value="">-- Artikel wählen --</option>
                {artikelListe.map(a => (
                  <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung} ({a.einheit})</option>
                ))}
              </select>
              <input
                type="number" step="0.01" min="0.01" className="menge-input" placeholder="Menge"
                value={neuePosition.menge} onChange={e => setNeuePosition(p => ({ ...p, menge: e.target.value }))}
              />
              <input
                type="number" step="0.01" min="0" className="preis-input"
                placeholder={artikelPreisEk(parseInt(neuePosition.artikel_id))?.toFixed(2) ?? 'Preis'}
                value={neuePosition.preis_einzeln} onChange={e => setNeuePosition(p => ({ ...p, preis_einzeln: e.target.value }))}
              />
              <button
                type="button" className="k-btn k-btn--primary k-btn--sm"
                disabled={zeileWirdGespeichert === -1 || !neuePosition.artikel_id}
                onClick={positionHinzufuegen}
              >+ Hinzufügen</button>
            </div>
          )}

          <div className="gesamtsumme-zeile">
            Gesamtsumme: <strong>{formatPrice(positionen.reduce((s, p) => s + (p.preis_gesamt || 0), 0))}</strong>
          </div>
        </div>
      )}
      </>
      )}

      {isEdit && ansichtModus === 'dokument' && editingBestellung && (
        <BelegDokumentView
          ansichtUrl={`${API_URL}/api/bestellungen/${editingBestellung.bestellung_id}/beleg-ansicht`}
          kontext={{ bestellungId: editingBestellung.bestellung_id }}
          onKonflikt={reloadBestellung}
        />
      )}

      {isEdit && editingBestellung && (
        <div style={{ marginTop: 24 }}>
          <AktivitaetenBlock entitaetTyp="bestellung" entitaetId={editingBestellung.bestellung_id} />
        </div>
      )}
    </div>
  );
};

export default BestellungForm;
